import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import copyToClipboard from 'util/clipboard/copyToClipboard';

const BUCKET_POLICY_EXAMPLE_JSON = JSON.stringify(
  {
    Sid: 'BenchlingConnect',
    Effect: 'Allow',
    Principal: {
      AWS: '<YOUR COPIED IAM ROLE>',
    },
    Action: ['s3:ListBucket', 's3:GetObject'],
    Resource: ['arn:aws:s3:::<YOUR BUCKET NAME>', 'arn:aws:s3:::<YOUR BUCKET NAME>/*'],
    Condition: {
      StringEquals: {
        'aws:PrincipalTag/TenantId': '<YOUR TENANT ID>',
      },
    },
  },
  null,
  2
);

interface ViewBucketInfoModalProps {
  roleArn: string;
  onRequestClose: () => void;
  gatewayName: string;
  bucketName: string;
}

export function ViewBucketInfoModal(props: ViewBucketInfoModalProps): JSX.Element {
  const {roleArn, onRequestClose} = props;

  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.PRIMARY,
          text: 'Close',
          cancel: true,
        },
      ]}
      title="S3 Bucket information"
      onRequestClose={() => {
        onRequestClose();
      }}
      closeable={true}
    >
      The following cloud gateway is connected to an AWS S3 bucket.
      <S3GatewayModalFields
        roleArn={roleArn}
        setGatewayName={() => {}}
        setBucketName={() => {}}
        gatewayName={props.gatewayName}
        bucketName={props.bucketName}
        viewOnly={true}
      />
    </Modal>
  );
}

interface CreateS3GatewayModalProps {
  roleArn: string;
  onRequestClose: () => void;
  onRequestSubmit: ({gatewayName, bucketName}: {gatewayName: string; bucketName: string}) => void;
}
export function CreateS3GatewayModal(props: CreateS3GatewayModalProps): JSX.Element {
  const {roleArn, onRequestClose, onRequestSubmit} = props;

  const [gatewayName, setGatewayName] = useState('');
  const [bucketName, setBucketName] = useState('');

  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.PRIMARY,
          text: 'Configure gateway',
          disabled: !gatewayName || !bucketName,
          submit: true,
        },
      ]}
      title="Configure cloud gateway"
      onRequestClose={() => {
        onRequestClose();
      }}
      closeable={true}
      onRequestSubmit={() => {
        onRequestSubmit({gatewayName, bucketName});
      }}
    >
      Create a gateway name and check the connection to the S3 bucket of choice. Once the connection has been
      made, the gateway will be usable. Copy the following IAM Role and add it to your S3 bucket so that
      Benchling can access the files with the appropriate permissions.
      <S3GatewayModalFields
        roleArn={roleArn}
        setGatewayName={setGatewayName}
        setBucketName={setBucketName}
        gatewayName={gatewayName}
        bucketName={bucketName}
        viewOnly={false}
      />
    </Modal>
  );
}

interface S3GatewayModalFieldsProps {
  roleArn: string;
  setGatewayName: (gatewayName: string) => void;
  setBucketName: (bucketName: string) => void;
  gatewayName: string;
  bucketName: string;
  viewOnly: boolean;
}
export function S3GatewayModalFields(props: S3GatewayModalFieldsProps): JSX.Element {
  const {roleArn, viewOnly, gatewayName, bucketName, setBucketName, setGatewayName} = props;

  const bucketPolicyExample = new Blob([BUCKET_POLICY_EXAMPLE_JSON], {type: 'application/json'});

  return (
    <>
      <br />
      <br />
      Use{' '}
      <a href={window.URL.createObjectURL(bucketPolicyExample)} target="_blank" rel="noopener noreferrer">
        this JSON snippet
      </a>{' '}
      for the new permission you'll need to add to your bucket policy. Be sure to replace{' '}
      <code>{'<YOUR COPIED IAM ROLE>'}</code>, <code>{'<YOUR BUCKET NAME>'}</code>, and{' '}
      <code>{'<YOUR TENANT ID>'}</code> with your specific values. Once updated, paste the updated JSON
      snippet into the <code>Statement</code> list of the bucket policy.
      <br />
      <br />
      For additional guidance, check out the{' '}
      <a
        href="https://docs.aws.amazon.com/AmazonS3/latest/userguide/access-policy-language-overview.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        AWS guidelines here.
      </a>
      <br />
      <br />
      <TextField
        label="Gateway name"
        isRequired={true}
        value={gatewayName}
        disabled={viewOnly}
        onChange={(e) => {
          setGatewayName(e.target.value);
        }}
      />
      <br />
      <div className={css(styles.roleArnBox)}>
        <TextField className={css(styles.roleArnField)} disabled={true} label="IAM Role" value={roleArn} />
        <Button
          className={css(styles.copyButton)}
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MEDIUM}
          onClick={() =>
            copyToClipboard(roleArn, {
              showToastOnSuccess: true,
              toastText: 'Copied IAM Role to clipboard.',
            })
          }
          text="Copy profile"
        />
      </div>
      <br />
      <TextField
        label="S3 Bucket name"
        isRequired={true}
        value={bucketName}
        disabled={viewOnly}
        onChange={(e) => {
          setBucketName(e.target.value);
        }}
      />
    </>
  );
}

export const styles = StyleSheet.create({
  roleArnBox: {
    display: 'flex',
    gridTemplateColumns: '4fr 1fr',
  },
  roleArnField: {
    flex: 1,
  },
  copyButton: {
    width: '97px',
    height: '28px',
    marginLeft: '10px',
    alignSelf: 'flex-end',
    fontSize: '12px',
    alignItems: 'center',
    boxSizing: 'border-box',
    padding: '0px 16px',
  },
});
